import React, { useState, useEffect, Suspense, lazy } from "react";
import { useSearchParams } from "react-router-dom";
import { Disc3, Grid3x3, AudioWaveform, ListMusic, Info, HelpCircle, BookOpen, SlidersHorizontal, Sparkles, Music4, Scissors, Gauge, FileMusic, Layers } from "lucide-react";
import TopBar from "@/studio/TopBar";
import Turntables from "@/studio/Turntables";
import PianoSynth from "@/studio/PianoSynth";
import Timeline from "@/studio/Timeline";
import EffectsRack from "@/studio/EffectsRack";
import DrumMachine from "@/studio/DrumMachine";
import RemixBar from "@/studio/RemixBar";
import Mixer from "@/studio/Mixer";
import PlaylistPlayer from "@/studio/PlaylistPlayer";
import Visualizer from "@/studio/Visualizer";
import OnboardingGuide from "@/studio/OnboardingGuide";
import Manual from "@/studio/Manual";
import InfoDot from "@/studio/InfoDot";
import Crate from "@/studio/Crate";
import HeadphoneCue from "@/studio/HeadphoneCue";
import MySongs from "@/studio/MySongs";
import Sampler from "@/studio/Sampler";
import Mastering from "@/studio/Mastering";
import SongSections from "@/studio/SongSections";
const Composer = lazy(() => import("@/studio/composer/Composer"));
import { useStudio } from "@/studio/StudioContext";
import { useSubscription } from "@/subscription/SubscriptionContext";
import useIsMobile from "@/hooks/useIsMobile";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const TABS = [
  {
    id: "decks",
    label: "Decks",
    icon: Disc3,
    hint: "Load a song onto a deck, press its play button, then drag the spinning record to scratch. Blend both decks with the Mixing Console below.",
  },
  {
    id: "beats",
    label: "Beats & Keys",
    icon: Grid3x3,
    hint: "Tap a preset or click grid squares to build a beat, then hit the green Play button up top. Play melodies on the piano — mouse or keys A S D F…",
  },
  {
    id: "timeline",
    label: "Timeline",
    icon: AudioWaveform,
    hint: "Record your mic or import audio into a track. Click “Add Track” for more lanes and rename them (Vocals, Bass…). Everything loops with Play.",
  },
  {
    id: "sections",
    label: "Sections",
    icon: Layers,
    hint: "Lay out your song — Intro, Verse, Hook, Drop, Bridge, Outro. Drag to reorder, pick a pattern and length for each, then Apply to arrange your beat.",
  },
  {
    id: "player",
    label: "Player",
    icon: ListMusic,
    hint: "Add your music files and play them like a playlist — your EQ and effects are applied while it plays.",
  },
  {
    id: "sampler",
    label: "Sampler",
    icon: Scissors,
    hint: "Vocal Remover — drop in any song and split it into instrumental, vocals, drums, bass and melody. Then chop any part into playable pads (Grid / Auto / Manual).",
  },
  {
    id: "master",
    label: "Master",
    icon: Gauge,
    hint: "Upload a finished song, shape the tone/loudness with presets, A/B against the original, then download a hi-def WAV.",
  },
  {
    id: "score",
    label: "Score",
    icon: FileMusic,
    hint: "See your beat written out as real sheet music. Pick a part, transpose it for Bb/Eb/F instruments, play it on real horns, and print parts for your band.",
  },
  {
    id: "songs",
    label: "My Songs",
    icon: Music4,
    hint: "All the songs you've saved. Tap one to reopen it, share it, or add AI cover art.",
  },
  {
    id: "manual",
    label: "Manual",
    icon: BookOpen,
    hint: "Full user guide, keyboard shortcuts and FAQ — read at your own pace.",
  },
];

function DecksView() {
  return (
    <div className="h-full flex flex-col overflow-y-auto p-4 md:p-6 gap-4">
      <div className="flex items-center justify-center gap-3">
        <div className="h-px flex-1 bg-[#262626]" />
        <span className="hw-label flex items-center gap-1.5">
          DJ Turntables — drag a spinning platter to scratch
          <InfoDot section="decks" label="How the turntables work" />
        </span>
        <div className="h-px flex-1 bg-[#262626]" />
      </div>

      {/* Decks on the left (side-by-side), Crate + Cue + scope as a right sidebar on wide screens */}
      <div className="flex flex-col xl:flex-row gap-5 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col items-center">
          <Turntables size={220} layout="row" />
        </div>
        <div className="w-full xl:w-[340px] shrink-0 flex flex-col gap-4">
          <Crate />
          <HeadphoneCue />
          <div>
            <span className="hw-label mb-2 block">Master Scope</span>
            <Visualizer height={90} variant="scope" />
          </div>
        </div>
      </div>
    </div>
  );
}

function BeatsView() {
  return (
    <div className="h-full flex flex-col min-h-0">
      <RemixBar />
      <div className="flex-1 min-h-0 border-b border-[#262626]">
        <DrumMachine />
      </div>
      <div className="h-[240px] shrink-0 metal-panel">
        <PianoSynth />
      </div>
    </div>
  );
}

export default function Studio() {
  const { engine, play, activeTab, setActiveTab, manualFocus } = useStudio();
  const { openUpgrade, sub } = useSubscription();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [trialDismissed, setTrialDismissed] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [guideAutostart, setGuideAutostart] = useState(false);

  useEffect(() => {
    if (params.get("upgrade") === "1") {
      openUpgrade();
      params.delete("upgrade");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pref = localStorage.getItem("nebula_guide_autostart");
    if (pref === null || pref === "1") setShowGuide(true);
    setGuideAutostart(pref === "1");
  }, []);

  const closeGuide = () => {
    setShowGuide(false);
    // if the user never opted in, remember not to auto-show again
    if (localStorage.getItem("nebula_guide_autostart") === null) {
      localStorage.setItem("nebula_guide_autostart", guideAutostart ? "1" : "0");
    }
  };

  const toggleAutostart = (on) => {
    setGuideAutostart(on);
    localStorage.setItem("nebula_guide_autostart", on ? "1" : "0");
  };

  const openManual = () => {
    setActiveTab("manual");
    closeGuide();
  };

  const runDemo = () => {
    engine.ensureStarted();
    if (engine.ctx && engine.ctx.state === "suspended") engine.ctx.resume();
    const hit = (...idx) => {
      const a = new Array(16).fill(false);
      idx.forEach((i) => (a[i] = true));
      return a;
    };
    engine.setPattern({ kick: hit(0, 4, 8, 12), clap: hit(4, 12), hat: hit(2, 6, 10, 14) });
    setActiveTab("beats");
    play();
    closeGuide();
  };

  const activeHint = TABS.find((t) => t.id === activeTab)?.hint;

  const showTrialBanner =
    !trialDismissed &&
    sub?.plan === "pro" &&
    sub?.trial_days_left != null &&
    sub.trial_days_left <= 2;

  return (
    <div className="h-screen w-full bg-[#0a0a0a] text-white overflow-hidden flex flex-col grain relative">
      <TopBar />

      {/* workspace tab bar */}
      <div className="h-11 bg-[#0d0d0d] border-b border-[#262626] flex items-center px-3 gap-1 shrink-0 relative z-20 overflow-x-auto no-scrollbar">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className="flex items-center gap-2 px-4 h-8 rounded-sm transition-colors relative shrink-0"
              style={{ background: active ? "#1c1c1c" : "transparent", color: active ? "#00e5ff" : "#737373" }}
            >
              <Icon size={15} />
              <span className="font-display text-xs uppercase tracking-widest font-bold">{t.label}</span>
              {active && (
                <div
                  className="absolute -bottom-[9px] left-2 right-2 h-0.5 bg-[#00e5ff]"
                  style={{ boxShadow: "0 0 8px #00e5ff" }}
                />
              )}
            </button>
          );
        })}
        <button
          data-testid="help-btn"
          onClick={() => setShowGuide(true)}
          className="ml-2 flex items-center gap-1.5 px-3 h-8 rounded-sm border border-[#333] text-neutral-400 hover:text-[#00e5ff] hover:border-[#00e5ff] transition-colors shrink-0"
          title="How to use BeatMXR"
        >
          <HelpCircle size={15} />
          <span className="font-display text-xs uppercase tracking-widest font-bold">Help</span>
        </button>
        <div className="ml-auto w-52 h-7 hidden lg:block">
          <Visualizer height={28} bars={40} />
        </div>
      </div>

      {/* contextual hint bar */}
      <div className="h-8 bg-[#0a0a0a] border-b border-[#1a1a1a] flex items-center px-4 gap-2 shrink-0 relative z-20">
        <Info size={13} className="text-[#00e5ff] shrink-0" />
        <span className="text-[11px] text-neutral-400 truncate" data-testid="tab-hint">
          {activeHint}
        </span>
      </div>

      {/* trial-ending banner */}
      {showTrialBanner && (
        <div
          data-testid="trial-banner"
          className="shrink-0 flex items-center justify-between gap-3 px-4 py-2 relative z-20"
          style={{ background: "linear-gradient(90deg, rgba(255,209,102,0.15), rgba(255,0,127,0.15))", borderBottom: "1px solid #262626" }}
        >
          <span className="text-[12px] text-neutral-200">
            <span className="font-bold text-[#ffd166]">Pro trial ends in {sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"}.</span>{" "}
            You'll keep Pro and your card will be charged when it ends — no action needed to continue.
          </span>
          <button
            data-testid="trial-banner-dismiss"
            onClick={() => setTrialDismissed(true)}
            className="text-neutral-400 hover:text-white text-xs font-bold shrink-0 px-2"
          >
            ✕
          </button>
        </div>
      )}

      {/* main: tab content + persistent FX rack (desktop) */}
      <div className="flex-1 flex min-h-0 relative z-10">
        <div className="flex-1 min-w-0 bg-[#050505]">
          {activeTab === "decks" && <DecksView />}
          {activeTab === "beats" && <BeatsView />}
          {activeTab === "timeline" && <Timeline />}
          {activeTab === "sections" && <SongSections />}
          {activeTab === "player" && <PlaylistPlayer />}
          {activeTab === "sampler" && <Sampler />}
          {activeTab === "master" && <Mastering />}
          {activeTab === "score" && (
            <Suspense fallback={<div className="p-8 text-neutral-500 text-sm">Loading score editor…</div>}>
              <Composer />
            </Suspense>
          )}
          {activeTab === "songs" && <MySongs />}
          {activeTab === "manual" && <Manual focus={manualFocus} />}
        </div>
        {!isMobile && (
          <div className="w-[320px] shrink-0 border-l border-[#262626] metal-panel overflow-hidden">
            <EffectsRack />
          </div>
        )}
      </div>

      {/* persistent mixing console (desktop) */}
      {!isMobile && (
        <div className="h-[286px] shrink-0 border-t border-[#262626] metal-panel relative z-10">
          <Mixer />
        </div>
      )}

      {/* mobile dock: slide-up Mixer + Effects drawers */}
      {isMobile && (
        <div className="h-16 shrink-0 border-t border-[#262626] metal-panel flex items-center justify-around px-3 gap-3 relative z-20">
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-mixer-btn"
                className="flex-1 flex items-center justify-center gap-2 h-11 rounded-sm border border-[#404040] text-neutral-200 font-display text-xs uppercase tracking-widest font-bold active:bg-[#242424] transition-colors"
              >
                <SlidersHorizontal size={16} className="text-[#00e5ff]" /> Mixer
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[#0d0d0d] border-[#262626] text-white p-0 h-[80vh]">
              <SheetTitle className="sr-only">Mixing Console</SheetTitle>
              <SheetDescription className="sr-only">Adjust channel volume, pan, mute and solo.</SheetDescription>
              <div className="h-full overflow-auto pt-9">
                <Mixer />
              </div>
            </SheetContent>
          </Sheet>
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-fx-btn"
                className="flex-1 flex items-center justify-center gap-2 h-11 rounded-sm border border-[#404040] text-neutral-200 font-display text-xs uppercase tracking-widest font-bold active:bg-[#242424] transition-colors"
              >
                <Sparkles size={16} className="text-[#ff007f]" /> Effects
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[#0d0d0d] border-[#262626] text-white p-0 h-[80vh]">
              <SheetTitle className="sr-only">Effects Rack</SheetTitle>
              <SheetDescription className="sr-only">EQ and effects for the selected channel.</SheetDescription>
              <div className="h-full overflow-auto pt-9">
                <EffectsRack />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}

      <OnboardingGuide
        open={showGuide}
        onClose={closeGuide}
        onDemo={runDemo}
        autostart={guideAutostart}
        onToggleAutostart={toggleAutostart}
        onManual={openManual}
      />
    </div>
  );
}
